import React, { useState, useEffect, useRef } from 'react';
import './PriorityDropdown.scss';

// Priority Configuration
export const PRIORITY_CONFIG = {
    0: { label: 'Low', shortLabel: 'Low', color: '#ffc53d', bgColor: '#fff4d6', number: '3' },
    1: { label: 'Medium', shortLabel: 'Med', color: '#ff9800', bgColor: '#ffe0b2', number: '2' },
    2: { label: 'High', shortLabel: 'High', color: '#f44336', bgColor: '#ffcdd2', number: '1' }
};

export const getPriorityLabel = (priority) => {
    return PRIORITY_CONFIG[priority]?.label || 'Low';
};

export const getPriorityShortLabel = (priority) => {
    return PRIORITY_CONFIG[priority]?.shortLabel || 'Low';
};

export const getPriorityColor = (priority) => {
    return PRIORITY_CONFIG[priority]?.color || '#ffc53d';
};

export const getPriorityBgColor = (priority) => {
    return PRIORITY_CONFIG[priority]?.bgColor || '#fff4d6';
};

export const getPriorityNumber = (priority) => {
    return PRIORITY_CONFIG[priority]?.number || '3';
};

const PriorityDropdown = ({ 
    currentPriority = 0, 
    onPriorityChange, 
    className = '',
    buttonClassName = '',
    showAsPill = true,
    handleShowDropdown = true
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showDropdown && 
                dropdownRef.current && 
                !dropdownRef.current.contains(event.target) &&
                !event.target.closest(`.${className || 'priority-dropdown-wrapper'}`)) {
                setShowDropdown(false);
                setSearchQuery('');
            }
        };

        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown, className]);

    const handlePrioritySelect = (newPriority) => {
        if (onPriorityChange) {
            onPriorityChange(newPriority);
        }
        setShowDropdown(false);
        setSearchQuery('');
    };

    // Filter priorities based on search query
    const filteredPriorities = Object.entries(PRIORITY_CONFIG).filter(([priority, config]) => {
        if (!searchQuery.trim()) return true;
        const searchLower = searchQuery.toLowerCase();
        return config.label.toLowerCase().includes(searchLower) || 
               config.shortLabel.toLowerCase().includes(searchLower);
    });

    const priorityConfig = PRIORITY_CONFIG[currentPriority ?? 0];

    return (
        <div className={`priority-dropdown-wrapper ${className}`} ref={dropdownRef}>
            {showAsPill ? (
                <button
                    type="button"
                    className={`priority-pill-btn ${buttonClassName}`}
                    onClick={() => handleShowDropdown ? setShowDropdown(!showDropdown) : null}
                    style={{ 
                        backgroundColor: priorityConfig.bgColor,
                        color: priorityConfig.color,
                        borderColor: priorityConfig.color
                    }}
                    title={getPriorityLabel(currentPriority ?? 0)}
                >
                    {/* <span className="priority-pill-number">{getPriorityNumber(currentPriority ?? 0)}</span> */}
                    <span className="priority-pill-label">{getPriorityShortLabel(currentPriority ?? 0)}</span>
                </button>
            ) : (
                <button
                    type="button"
                    className={`priority-dropdown-btn ${buttonClassName}`}
                    onClick={() => handleShowDropdown ? setShowDropdown(!showDropdown) : null}
                    style={{ 
                        backgroundColor: priorityConfig.bgColor,
                        color: priorityConfig.color,
                        borderColor: priorityConfig.color
                    }}
                    title={getPriorityLabel(currentPriority ?? 0)}
                >
                    {getPriorityLabel(currentPriority ?? 0)}
                </button>
            )}
            {showDropdown && (
                <div className="priority-dropdown">
                    <input
                        type="text"
                        className="priority-dropdown-search"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        autoFocus
                    />
                    <div className="priority-dropdown-list">
                        {filteredPriorities.map(([priority, config]) => {
                            const priorityNum = parseInt(priority, 10);
                            const isSelected = (currentPriority ?? 0) === priorityNum;
                            return (
                                <div
                                    key={priority}
                                    className={`priority-dropdown-item${isSelected ? ' selected' : ''}`}
                                    onClick={() => handlePrioritySelect(priorityNum)}
                                >
                                    <span 
                                        className="priority-item-pill"
                                        style={{ 
                                            backgroundColor: config.bgColor,
                                            color: config.color,
                                            borderColor: config.color
                                        }}
                                    >
                                        {/* <span className="priority-pill-number">{config.number}</span> */}
                                        <span className="priority-pill-label">{config.shortLabel}</span>
                                    </span>
                                    {isSelected && (
                                        <i className="fa-solid fa-check" style={{ marginLeft: 'auto' }}></i>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PriorityDropdown;

